import {
  RolloverOptions,
  PolicyPeriodDuration,
  PolicyPeriodDurationDisplay
} from 'Utils/types/benefitProgramPolicy'
import { Purchase } from 'Utils/types/purchase'
import { DepositAccount } from 'Utils/types/connectedAccounts'
import { Reimbursement } from 'Utils/types/reimbursement'
import {
  displayDate,
  displayMonthAndYear,
  firstOfPreviousMonth,
  sameMonth
} from 'Utils/helpers/dateUtils'
import { InvoiceState, LatestInvoice } from 'Utils/types/invoice'
import { numToCurrency } from 'Utils/helpers/currency'
import { EmployerBenefitProgram, UserBenefitProgram } from 'Utils/types/benefitProgram'
import { DollarAmount } from 'Utils/types/common'
import { CurrencyCode } from 'Utils/types/currencies'

import startOfYear from 'date-fns/startOfYear'
import startOfQuarter from 'date-fns/startOfQuarter'
import startOfMonth from 'date-fns/startOfMonth'

export const getProgramPercentageUsed = (allowance: DollarAmount, reimbursement: DollarAmount) => {
  if (reimbursement === 0) {
    return 0
  }

  // Don't allow return of percentage > 100
  return Math.min((reimbursement / allowance) * 100, 100)
}

export const calcSumOfPurchases = (purchases: Purchase[] = []) => {
  return purchases.reduce((sum, purchase) => {
    return sum + purchase.amount
  }, 0)
}

type UsageMessage = {
  program: UserBenefitProgram
  lastMonthsInvoice: LatestInvoice
  lastMonthsReimbursement?: Reimbursement
  depositAccount?: DepositAccount
  isPayrollReimbursement?: boolean
  locale: string
}

export const startOfAnnualPeriod = (date: Date, annualResetMonth: number): Date => {
  const month = annualResetMonth - 1
  const year = date.getMonth() >= month ? date.getFullYear() : date.getFullYear() - 1

  return new Date(year, month, 1)
}

export const isStartOfNonMonthlyPeriod = (
  duration: PolicyPeriodDuration,
  annualResetMonth: number,
  policyPeriodStartDate?: string
) => {
  if (duration === PolicyPeriodDuration.Monthly) return false

  const now = new Date()

  if (policyPeriodStartDate) {
    const periodStart = new Date(policyPeriodStartDate)
    return sameMonth(periodStart, now)
  }

  if (duration === PolicyPeriodDuration.Quarterly) {
    return sameMonth(startOfQuarter(now), now)
  }

  if (duration === PolicyPeriodDuration.Yearly) {
    return sameMonth(startOfAnnualPeriod(now, annualResetMonth), now)
  }
}

export const getReimbursementMessage = (
  reimbursementAmount: string,
  currencyCode: CurrencyCode
) => {
  const monthYear = displayMonthAndYear(firstOfPreviousMonth())
  const reimbursementMessage =
    currencyCode === CurrencyCode.USDollar
      ? `Your ${reimbursementAmount} reimbursement for ${monthYear} should be deposited soon.`
      : `Your reimbursement of approximately ${reimbursementAmount} for ${monthYear} should be deposited soon.`

  return reimbursementMessage
}

export const generateIncompleteInvoiceMessage = (
  allowanceUsed: string,
  hideStatusMessage: boolean
) => {
  return (
    `You used ${allowanceUsed} of your allowance in ${displayMonthAndYear(
      firstOfPreviousMonth()
    )}.` + (hideStatusMessage ? '' : ` Check back soon to see your reimbursement status.`)
  )
}

export const generateNoDepositAccountMessage = (allowanceUsed: string) => {
  return (
    `You used ${allowanceUsed} of your allowance in ${displayMonthAndYear(
      firstOfPreviousMonth()
    )}. ` + `You must connect your deposit account to receive the reimbursement.`
  )
}

export const generateNoReimbursementMessage = () => {
  return `You did not use your allowance in ${displayMonthAndYear(
    firstOfPreviousMonth()
  )} and will not be receiving a reimbursement.`
}

export const generateRolloverPendingMessage = (
  allowanceUsed: string,
  rollover: string,
  hideStatusMessage: boolean
) => {
  return (
    `You used ${allowanceUsed} of your allowance in ${displayMonthAndYear(
      firstOfPreviousMonth()
    )}, ` +
    `with an estimated ${rollover} rolling over.` +
    (hideStatusMessage ? '' : ` Check back soon to see your reimbursement status.`)
  )
}

export const generatePastUsageMessage = ({
  program,
  lastMonthsReimbursement,
  lastMonthsInvoice,
  depositAccount,
  isPayrollReimbursement = false,
  locale
}: UsageMessage) => {
  const {
    previousPeriodAllowance,
    previousTotalAllowance,
    currentPeriodAllowance,
    currentTotalAllowance,
    previousMonthReimbursement
  } = program

  // If the program hasn't started yet, the rest doesn't matter. It wasn't eligible.
  if (
    previousPeriodAllowance === 0 &&
    previousTotalAllowance === 0 &&
    (currentTotalAllowance > 0 || currentPeriodAllowance > 0)
  ) {
    return 'This benefits program was not available last month.'
  }

  const now = new Date()

  const isInvoicePaid =
    lastMonthsInvoice.state && lastMonthsInvoice.state === InvoiceState.Completed
  const isReceivingReimbursement =
    !!lastMonthsReimbursement && !!program.previousMonthPurchases.length
  const hasRollover =
    program.membership.rollover !== RolloverOptions.Disabled &&
    !(program.membership.rollover === RolloverOptions.EndOfYear && sameMonth(now, startOfYear(now)))
  const used = Math.min(previousMonthReimbursement, previousTotalAllowance)
  const allowanceUsed = numToCurrency(used, program.currencyCode, locale)
  const rolloverAmount = previousTotalAllowance - program.previousMonthReimbursement

  // The invoice is in Open, Draft, Processing, or Preparing state (waiting to be paid), the program has rollover, and the user
  // has an amount rolling over.
  if (lastMonthsInvoice.state !== InvoiceState.Completed && hasRollover && rolloverAmount > 0) {
    return generateRolloverPendingMessage(
      allowanceUsed,
      numToCurrency(rolloverAmount, program.currencyCode, locale),
      isPayrollReimbursement
    )
  }

  // The user is going to receive a reimbursement but the invoice is draft/open/processing.
  if (!isInvoicePaid && isReceivingReimbursement) {
    return generateIncompleteInvoiceMessage(allowanceUsed, isPayrollReimbursement)
  }

  if (isInvoicePaid && isReceivingReimbursement) {
    const reimbursementAmount = numToCurrency(
      previousMonthReimbursement,
      program.currencyCode,
      locale
    )

    if (!depositAccount && !isPayrollReimbursement) {
      // The invoice is paid and the user is going to receive a reimbursement. However, they have no deposit account
      // connected so we want to make this even more known.
      return generateNoDepositAccountMessage(allowanceUsed)
    }
    // The invoice is paid and the user is going to receive a reimbursement.
    return getReimbursementMessage(reimbursementAmount, program.currencyCode)
  }

  // The user didn't use their allowance at all and won't be receiving a reimbursement.
  return generateNoReimbursementMessage()
}

export const isBenefitProgramActive = (program: EmployerBenefitProgram) =>
  !program.policy.scheduledEndDate || program.policy.scheduledEndDate > new Date()

export const periodStartForDuration = (
  date: Date,
  duration: PolicyPeriodDuration,
  annualResetMonth: number
): Date => {
  switch (duration) {
    case PolicyPeriodDuration.Quarterly:
      return startOfQuarter(date)
    case PolicyPeriodDuration.Yearly:
      return startOfAnnualPeriod(date, annualResetMonth)
    default:
      return startOfMonth(date)
  }
}

export const programPeriodHeading = ({ membership }: UserBenefitProgram, today = new Date()) => {
  if (
    membership.policyPeriodDuration === PolicyPeriodDuration.Yearly &&
    membership.annualResetMonth !== 1
  ) {
    const start = startOfAnnualPeriod(today, membership.annualResetMonth)
    const end = new Date(start.getFullYear(), start.getMonth() + 11, 1)

    return `${displayMonthAndYear(start)} - ${displayMonthAndYear(end)}`
  }

  if (membership.policyPeriodDuration !== PolicyPeriodDuration.OneTime) {
    return `This ${PolicyPeriodDurationDisplay.get(membership.policyPeriodDuration)}`
  }

  return membership.scheduledEndDate
    ? `Use by ${displayDate(membership.scheduledEndDate)}`
    : 'One-Time Benefit'
}
